import cx from 'classnames'
import { useRef, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'

import { capitalize } from 'lodash'

import Styles from './GenerateImageModal.module.scss'

import { ASPECT_RATIOS, ExamplePrompts, IMAGE_TYPES, PRIORITIES } from 'components/GenerateImageDrawer/Form'

import { IconButton } from 'components/IconButton/IconButton'
import { Send2 } from 'blaze-ui/icons/24'
import { ChipMenu } from 'components/GenerateImageDrawer/components/ChipMenu'
import { GenerationSets } from 'components/GenerateImageDrawer/GenerationSets'
import { UseGenerateImagesResult, useGenerateImages } from 'components/GenerateImageDrawer/hooks/useGenerateImages'
import { PreviewImageModal } from './components/PreviewImageModal/PreviewImageModal'
import { GeneratedImage } from 'types'
import { AiConfirmModal } from 'components/ImagePickerModal/AIGeneration/AiConfirmModal'
import { useConfirmModal } from 'hooks/useConfirmModal'
import { Chip } from 'components/GenerateImageDrawer/components/Chip'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Carousel } from 'components/Carousel/Carousel'
import { Text } from 'blaze-ui/Text/Text'

export const TRENDING_EXAMPLES = [
  {
    prompt: 'Happy customer holding a product',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464582/ai_generation_examples/happy_customer_holding_a_product_xoolfe.png',
  },
  {
    prompt: 'Overhead shot of artist’s worktable',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464583/ai_generation_examples/overhead_shot_of_artists_worktable_hgbces.png',
  },
  {
    prompt: 'E-commerce summer sale promotion',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464582/ai_generation_examples/ecommerce_summer_sale_tmtaao.png',
  },
  {
    prompt: 'Happy hour at a restaurant',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464583/ai_generation_examples/happy_hour_at_a_restaurant_alhfd9.png',
  },
  {
    prompt: 'Fitness studio launching online classes',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464582/ai_generation_examples/fitness_studio_launching_online_classes_fegtuv.png',
  },
  {
    prompt: 'Moodboard for marketing agency',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464583/ai_generation_examples/moodboard_for_marketing_agency_tl0yxd.png',
  },
  {
    prompt: 'Adopt a pet day at local animal shelter',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746464582/ai_generation_examples/adopt_a_pet_day_at_local_animal_shelter_bxkw3p.png',
  },
  {
    prompt: 'Open house for real estate',
    mime_type: 'image/png',
    imageUrl:
      'https://res.cloudinary.com/almanac/image/upload/v1746465120/ai_generation_examples/open_house_for_real_estate_zaq8dj.png',
  },
]

export interface GenerateImageModalContentProps extends UseGenerateImagesResult {
  showExamples?: boolean
  onConfirm: (img: GeneratedImage) => void
  containerRef: React.RefObject<HTMLDivElement>
}

export const GenerateImageModalContent = (props: GenerateImageModalContentProps) => {
  const { openModal } = useModals()
  const [prompt, setPrompt] = useState('')
  const isGptImageOneEnabled = useFlipperFlag('gpt_image_one')
  const [imageAspectRatio, setImageAspectRatio] = useState(ASPECT_RATIOS[0])
  const [imageType, setImageType] = useState(IMAGE_TYPES[0])
  const [priority, setPriority] = useState('speed')
  const {
    handleMultiGenerate,
    onConfirm,
    onGenerateMoreLikeThis,
    generationSets,
    containerRef,
    setGenerationSets,
    onSaveImage,
    onDeleteImage,
    onCancelGeneration,
    onRetry,
    removeProcessingId,
    isProcessing,
    showExamples = true,
  } = props

  const handleSubmit = () => {
    const values = {
      generation_type: 'image',
      topic: prompt,
      image_type: imageType,
      image_aspect_ratio: imageAspectRatio,
      priority: priority,
    }
    handleMultiGenerate(values)
    setPrompt('')
  }

  const openImageModal = (img: GeneratedImage, requestId: number) => {
    openModal(
      PreviewImageModal,
      {
        img: { ...img, requestId },
        onUseImage: (img: GeneratedImage) => {
          onConfirm(img)
        },
        onGenerateMoreLikeThis,
      },
      {
        newStack: true,
      },
    )
  }

  return (
    <Modal.Content className={Styles.content}>
      <div className={Styles.container} ref={containerRef}>
        {generationSets.length === 0 && (
          <>
            <p className={Styles.description}>Describe the image you want to generate—or try an example</p>
            {showExamples &&
              ExamplePrompts.map((prompt) => (
                <div key={prompt} className={Styles.chipWrapper}>
                  <Chip onPress={() => setPrompt(prompt)}>
                    <span className={Styles.promptLabel}>{prompt}</span>
                  </Chip>
                </div>
              ))}
          </>
        )}
        {generationSets.length > 0 && (
          <GenerationSets
            setGenerationSets={setGenerationSets}
            generationSets={generationSets}
            onConfirm={(img) => onConfirm(img)}
            onSaveImage={onSaveImage}
            onImagePress={openImageModal}
            onDeleteImage={onDeleteImage}
            onGenerateMoreLikeThis={onGenerateMoreLikeThis}
            onCancelGeneration={onCancelGeneration}
            onRetry={onRetry}
            numColumns="four"
            showProcessingText
            removeProcessingId={removeProcessingId}
          />
        )}
      </div>
      <div className={cx(Styles.form, { [Styles.disabled]: isProcessing })}>
        <input
          className={Styles.input}
          placeholder="Describe an image"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyUp={(e) => e.key === 'Enter' && prompt.trim().length > 0 && handleSubmit()}
          disabled={isProcessing}
          autoFocus
        />
        <div className={Styles.buttonContainer}>
          <div className={Styles.chipMenus}>
            <ChipMenu
              type="Size"
              label={capitalize(imageAspectRatio)}
              selectedOption={imageAspectRatio}
              options={ASPECT_RATIOS}
              onChange={(option) => setImageAspectRatio(option)}
              isDisabled={isProcessing}
            />
            <ChipMenu
              type="Style"
              label={capitalize(imageType)}
              selectedOption={imageType}
              options={IMAGE_TYPES}
              onChange={(option) => setImageType(option)}
              isDisabled={isProcessing}
            />
            {isGptImageOneEnabled && (
              <ChipMenu
                type="Priority"
                label={capitalize(priority)}
                selectedOption={priority}
                options={PRIORITIES}
                onChange={(option) => setPriority(option)}
                isDisabled={isProcessing}
              />
            )}
          </div>
          <IconButton
            icon={Send2}
            onPress={() => handleSubmit()}
            isDisabled={isProcessing || prompt.trim().length === 0}
            data-testid="submit-button"
          />
        </div>
      </div>
      {!showExamples && generationSets.length === 0 && (
        <div className={Styles.trendingExamples}>
          <Heading level={4}>Trending Examples</Heading>
          <Carousel paddingLeft="4px">
            {TRENDING_EXAMPLES.map((trendingExample) => (
              <div key={trendingExample.imageUrl} onClick={() => setPrompt(trendingExample.prompt)}>
                <div className={Styles.trendingCard}>
                  <img src={trendingExample.imageUrl} alt="" />
                  <Text variant="secondary">{trendingExample.prompt}</Text>
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </Modal.Content>
  )
}

export const GenerateImageModal = ({ onConfirm }: { onConfirm: (img: GeneratedImage) => void }) => {
  const { closeModal } = useModals()
  const aiConfirmProvider = useConfirmModal(AiConfirmModal)
  const containerRef = useRef<HTMLDivElement>(null)
  const props = useGenerateImages({ containerRef })

  const showConfirmModalIfNecessary = async () => {
    if (props.generationSets.length > 0) {
      const confirm = await aiConfirmProvider()

      if (confirm) {
        closeModal()
      }

      return
    }

    closeModal()
  }

  return (
    <Modal.Root onPressOutside={showConfirmModalIfNecessary} blaze size="md" onClose={showConfirmModalIfNecessary}>
      <Modal.Header title="Generate an Image" />
      <GenerateImageModalContent onConfirm={onConfirm} containerRef={containerRef} {...props} />
    </Modal.Root>
  )
}
